import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import {
    ClearFields,
    MultipleOptionField,
    MultipleOptionItem,
    SingleHorizontalOptionItem,
    SingleOptionField,
    SingleOptionItem,
} from '@connected/form-fields/components/option';
import { useTranslation } from 'react-i18next';

import { useOffline } from '@/stores/network-status';

import { useAdditionalTopics } from '../hooks/use-additional-topics';
import { useInspectionKindForm } from '../hooks/use-inspection-kind-form';
import { useInspectionReports } from '../hooks/use-inspection-reports';
import { RestrictedMultipleOptionItem } from './restricted-option-item';

export function RequestConcerningForm(props: { projectVersionId: string }) {
    const { projectVersionId } = props;

    const { t } = useTranslation('applications');

    const isUserOffline = useOffline();

    const { context } = useElectricalProject();

    const {
        inspectionOptions,
        handleTemplateChange,
        handleMultipleTemplatesChange,
        isLoading: isInspectionReportsLoading,
        isPending: isTemplateMutationPending,
    } = useInspectionReports(projectVersionId);

    const {
        options: additionalTopicOptions,
        selectedValues: additionalTopicSelectedValues,
        handleChange: handleAdditionalTopicsChange,
    } = useAdditionalTopics(projectVersionId);

    const {
        form,
        patchField,
        validators,
    } = useInspectionKindForm(projectVersionId);

    const isDisabled = isTemplateMutationPending || isInspectionReportsLoading || isUserOffline;

    return (
        <section className="flex flex-col gap-4">
            <Label label={t('request-concerning.form.inspectionType.label')} htmlFor="inspectionKind" className="relative">
                <form.Field
                    name="inspectionKind"
                    validators={{
                        onChange: validators.inspectionKind,
                        onChangeAsync: patchField('InspectionKind'),
                        onChangeAsyncDebounceMs: patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <>
                            <ClearFields onClear={() => handleChange(null)} />
                            <SingleOptionField
                                options={[
                                    { value: 'Inspection', label: t('request-concerning.form.inspectionType.options.newInspection') },
                                    { value: 'RepeatInspection', label: t('request-concerning.form.inspectionType.options.repeatInspection') },
                                ]}
                                handleChange={handleChange}
                                className="w-full flex flex-row"
                                state={state}
                                name={name}
                                render={SingleHorizontalOptionItem}
                                disabled={isDisabled}
                            />
                            <FieldInfo state={state} />
                        </>
                    )}
                </form.Field>
            </Label>

            <Label label={t('request-concerning.form.instructions.label')} className="relative">
                {!context.isCombinationInspection
                    ? (
                            <SingleOptionField
                                options={inspectionOptions}
                                handleChange={values => handleTemplateChange(values as string)}
                                state={{ value: context.selectedTemplates[0]?.electricalProjectDocumentTemplateId }}
                                render={SingleOptionItem}
                                disabled={isDisabled}
                            />
                        )
                    : (
                            <MultipleOptionField
                                options={inspectionOptions}
                                handleChange={newState => handleMultipleTemplatesChange(newState)}
                                state={{ value: context.selectedTemplates.map(x => x.electricalProjectDocumentTemplateId) }}
                                render={({ option, selected, onSelect, name }) => (
                                    <RestrictedMultipleOptionItem
                                        option={option as Parameters<typeof RestrictedMultipleOptionItem>[0]['option']}
                                        selected={selected}
                                        onSelect={onSelect}
                                        name={name}
                                    />
                                )}
                                disabled={isDisabled}
                                emptyOptionsMessage={t('request-concerning.form.instructions.emptyMessage')}
                                placeholder={t('request-concerning.form.instructions.placeholder')}
                            />
                        )}
            </Label>
            <Label
                label={t(
                    'request-concerning.form.additional-instructions.label',
                )}
                htmlFor="additional-instructions"
            >
                <MultipleOptionField
                    options={additionalTopicOptions}
                    handleChange={handleAdditionalTopicsChange}
                    state={{ value: additionalTopicSelectedValues }}
                    render={MultipleOptionItem}
                    disabled={isUserOffline}
                />
            </Label>
        </section>
    );
}
